import {
  SSR_BOOTSTRAP_ELEMENT_ID,
  SSR_BOOTSTRAP_VERSION,
  type Event,
  type EventListResponse,
  type TerritoryBootstrapBody,
  type TerritoryCapabilities,
} from '@buzzmap/shared';
import { isSsrShellForeign } from './ssrShell';

/**
 * First-view data embedded in the SSR document (T204-INT, client half).
 *
 * The server writes a `<script type="application/json" id="ssr-bootstrap">`
 * block with the answers the app would otherwise request one after the other
 * right after its JavaScript runs (capabilities, the territory resolution, the
 * "Imperdibili" gallery, the event body, the first Esplora feed page). Each
 * part carries the request that produced it; a consumer uses a part ONLY when
 * the request it is about to make equals that string character for character.
 *
 * Safety rules, all fail-closed (any doubt = the fetch of today):
 *
 * - The block is read ONCE, at module load, with `JSON.parse` (never evaluated,
 *   never put in `innerHTML`). Malformed JSON, an unknown `v`, a bad shape or a
 *   `path` that is not the document's own pathname (`isSsrShellForeign`) make
 *   every accessor return `undefined`.
 * - Every part is consumable ONCE. Nothing is served after a client navigation
 *   (the current pathname must still be the document's) or after
 *   `discardSsrBootstrap()` (a registry-version change or a 426).
 * - `take*` accessors consume only on an exact match; a mismatch leaves the
 *   part for a later, matching call and returns `undefined`.
 * - `peek*` + `consume*` exist for the two parts the provider needs during
 *   render (capabilities, territory resolution): render must stay pure, so the
 *   read is a `peek` and the consumption happens in a mount effect. That is
 *   also what makes the double render/effect of StrictMode harmless.
 */

type PartName = 'caps' | 'territory' | 'gallery' | 'event' | 'feed';

interface ValidatedBlock {
  caps: TerritoryCapabilities;
  territory?: { request: string; body: TerritoryBootstrapBody };
  gallery?: { query: string; events: Event[] };
  event?: { id: string; body: Event };
  feed?: { query: string; day: string; body: EventListResponse };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCapabilities(value: unknown): value is TerritoryCapabilities {
  return (
    isRecord(value) &&
    value.ready === true &&
    typeof value.activeRegistryVersion === 'string' &&
    value.activeRegistryVersion.length > 0 &&
    typeof value.minimumDiscoveryProtocol === 'number' &&
    Number.isFinite(value.minimumDiscoveryProtocol)
  );
}

function isTerritoryBody(value: unknown): value is TerritoryBootstrapBody {
  return (
    isRecord(value) &&
    typeof value.canonicalPath === 'string' &&
    typeof value.scope === 'string' &&
    isRecord(value.location)
  );
}

function isEventLike(value: unknown): value is Event {
  return isRecord(value) && typeof value.id === 'string' && typeof value.category === 'string';
}

function parseTerritory(value: unknown): ValidatedBlock['territory'] {
  if (!isRecord(value) || typeof value.request !== 'string' || !value.request) return undefined;
  if (!isTerritoryBody(value.body)) return undefined;
  return { request: value.request, body: value.body };
}

function parseGallery(value: unknown): ValidatedBlock['gallery'] {
  if (!isRecord(value) || typeof value.query !== 'string' || !value.query) return undefined;
  if (!Array.isArray(value.events) || !value.events.every(isEventLike)) return undefined;
  return { query: value.query, events: value.events };
}

function parseEvent(value: unknown): ValidatedBlock['event'] {
  if (!isRecord(value) || typeof value.id !== 'string' || !value.id) return undefined;
  if (!isRecord(value.body) || value.body.id !== value.id) return undefined;
  return { id: value.id, body: value.body as unknown as Event };
}

function parseFeed(value: unknown): ValidatedBlock['feed'] {
  if (!isRecord(value) || typeof value.query !== 'string' || !value.query) return undefined;
  if (typeof value.day !== 'string' || !value.day) return undefined;
  const body = value.body;
  if (!isRecord(body) || !Array.isArray(body.events) || !body.events.every(isEventLike)) {
    return undefined;
  }
  if (typeof body.total !== 'number' || !Number.isFinite(body.total)) return undefined;
  return { query: value.query, day: value.day, body: body as unknown as EventListResponse };
}

/** Parse and validate the raw JSON text; `null` for anything not exactly the contract. */
function parseBlock(text: string): ValidatedBlock | null {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(raw) || raw.v !== SSR_BOOTSTRAP_VERSION) return null;
  if (typeof raw.path !== 'string' || isSsrShellForeign(raw.path)) return null;
  if (!isCapabilities(raw.caps)) return null;
  // A part that fails its own check is dropped alone; the others stay usable.
  return {
    caps: raw.caps,
    territory: parseTerritory(raw.territory),
    gallery: parseGallery(raw.gallery),
    event: parseEvent(raw.event),
    feed: parseFeed(raw.feed),
  };
}

function readBlock(): ValidatedBlock | null {
  if (typeof document === 'undefined') return null;
  const text = document.getElementById(SSR_BOOTSTRAP_ELEMENT_ID)?.textContent;
  return text ? parseBlock(text) : null;
}

// Captured once at module load. This module is in the entry chunk (like
// `ssrShell`), and the deferred module script is evaluated after the document
// was parsed, so the block is already in the DOM.
let block: ValidatedBlock | null = readBlock();
const consumed = new Set<PartName>();

/** The embedded data belongs to the document: not once the user has moved elsewhere. */
function stillOnDocumentPath(): boolean {
  return typeof window !== 'undefined' && !isSsrShellForeign(window.location.pathname);
}

/** The part if it is present, unconsumed and the page is still the document's own. */
function available<K extends PartName>(name: K): ValidatedBlock[K] | undefined {
  if (!block || consumed.has(name) || !stillOnDocumentPath()) return undefined;
  return block[name];
}

/**
 * Capabilities the document was built with, or `undefined`. Pure read: for
 * the provider's first render. Use only as "last good" capabilities.
 */
export function peekSsrCaps(): TerritoryCapabilities | undefined {
  return available('caps');
}

/** Territory resolution the document was built with (`request` + response body). Pure read. */
export function peekSsrTerritory(): { request: string; body: TerritoryBootstrapBody } | undefined {
  return available('territory');
}

/**
 * Mark capabilities and territory resolution consumed (call from a mount
 * effect, after the provider seeded its state from the two `peek*` reads).
 */
export function consumeSsrStart(): void {
  consumed.add('caps');
  consumed.add('territory');
}

/**
 * The embedded gallery events (already diversified) when `query` is exactly the
 * one the server recorded; consumes them. `undefined` otherwise.
 */
export function takeSsrGallery(query: string): Event[] | undefined {
  const part = available('gallery');
  if (!part || part.query !== query) return undefined;
  consumed.add('gallery');
  return part.events;
}

/**
 * The embedded first feed page when `query` is exactly the recorded one AND the
 * document was built on `today` (the feed query carries no date of its own).
 * Consumes it. `undefined` otherwise.
 */
export function takeSsrFeed(query: string, today: string): EventListResponse | undefined {
  const part = available('feed');
  if (!part || part.query !== query || part.day !== today) return undefined;
  consumed.add('feed');
  return part.body;
}

/** The embedded event body when `id` is the recorded one; consumes it. */
export function takeSsrEvent(id: string): Event | undefined {
  const part = available('event');
  if (!part || part.id !== id) return undefined;
  consumed.add('event');
  return part.body;
}

/**
 * Drop everything that has not been used yet. Called when the epoch is known to
 * have moved (new registry version, HTTP 426): data built for another epoch
 * must not be served afterwards.
 */
export function discardSsrBootstrap(): void {
  block = null;
}

/**
 * Read the block from the document again and forget what was consumed or
 * discarded. For tests: production reads the document exactly once, at load.
 */
export function reloadSsrBootstrapFromDocument(): void {
  block = readBlock();
  consumed.clear();
}
